import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, inject, Injectable, isDevMode, LOCALE_ID, NgZone } from '@angular/core';
import { CountryDialCode } from '@models/store.model';
import { HttpService } from '@services/http.service';
import {
    BehaviorSubject,
    combineLatest,
    delay,
    distinctUntilChanged,
    filter,
    fromEvent,
    interval,
    map,
    merge,
    mergeMap,
    Observable,
    of,
    startWith,
    switchMap,
    take,
    withLatestFrom,
} from 'rxjs';
import { DialogService } from './dialog.service';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    Data,
    NavigationEnd,
    Router,
} from '@angular/router';
import { isSSRMode } from '@constants/app.constant';

@Injectable({
    providedIn: 'root',
})
export class CommonService {
    private readonly _isServer = !inject(Platform).isBrowser;
    private readonly _warningOpened$ = new BehaviorSubject<boolean>(false);
    private readonly MAXIMUM_IDLE_TIME = 10 * 60000;

    constructor(
        @Inject(DOCUMENT) private readonly _document: Document,
        @Inject(LOCALE_ID) private readonly _localeId: string,
        private readonly _zone: NgZone,
        private readonly _http: HttpService,
        private readonly _httpClient: HttpClient,
        private readonly _dialog: DialogService,
        private readonly _router: Router,
        private readonly _route: ActivatedRoute
    ) {}

    /**
     * Get airline labels
     *
     * @returns {Observable<Record<string, string>}
     * @memberof CommonService
     */
    public getAirlineLabels(): Observable<Record<string, string>> {
        const url = !isSSRMode
            ? `/assets/data/airline-labels.json`
            : '/api/master-data/airline-labels';
        return this._httpClient.get<Record<string, string>>(url);
    }

    /**
     * Get country dia code
     *
     * @return {*}  {Observable<Record<string, CountryDialCode>>}
     * @memberof CommonService
     */
    public getCountryDialCode(): Observable<Record<string, CountryDialCode>> {
        const url = !isSSRMode
            ? `/assets/data/country-dial-code.json`
            : '/api/master-data/countries';
        return this._httpClient.get<Record<string, CountryDialCode>>(url);
    }

    /**
     * Update canonical link
     *
     * @param {string} link
     * @memberof CommonService
     */
    public updateCanonicalLink(link: string) {
        this._zone.runOutsideAngular(() => {
            const canonicalLink = this._document.querySelector('link[rel="canonical"]');
            if (canonicalLink) {
                canonicalLink.setAttribute('href', link);
            } else {
                const linkElement = this._document.createElement('link');
                linkElement.setAttribute('rel', 'canonical');
                linkElement.setAttribute('href', link);
                this._document.head.appendChild(linkElement);
            }
        });
    }

    /**
     * Update meta tag
     *
     * @param {string} name
     * @param {string} content
     * @memberof CommonService
     */
    public updateMetaTag(name: string, content: string) {
        this._zone.runOutsideAngular(() => {
            const metaTag = this._document.querySelector(`meta[name="${name}"]`);
            if (metaTag) {
                metaTag.setAttribute('content', content);
            } else {
                const metaElement = this._document.createElement('meta');
                metaElement.setAttribute('name', name);
                metaElement.setAttribute('content', content);
                this._document.head.appendChild(metaElement);
            }
        });
    }

    /**
     * Update alternate link
     *
     * @param {{ link: string; hreflang: string }[]} links
     * @memberof CommonService
     */
    public updateAlternateLink(links: { link: string; hreflang: string }[]) {
        this._zone.runOutsideAngular(() => {
            const alternateLinks = this._document.querySelectorAll('link[rel="alternate"]');
            alternateLinks.forEach((link) => {
                link.remove();
            });

            links.forEach(({ link, hreflang }) => {
                const linkElement = this._document.createElement('link');
                linkElement.setAttribute('rel', 'alternate');
                linkElement.setAttribute('href', link);
                linkElement.setAttribute('hreflang', hreflang);
                this._document.head.appendChild(linkElement);
            });
        });
    }

    /**
     * Retrieves the current route data from the given snapshot.
     *
     * @private
     * @param {ActivatedRouteSnapshot} snapshot - The snapshot of the current route.
     * @returns {Data} The data of the current route.
     */
    private getCurrentRouteData(snapshot: ActivatedRouteSnapshot): Data {
        if (snapshot.firstChild) {
            return this.getCurrentRouteData(snapshot.firstChild);
        }
        return snapshot.data;
    }

    /**
     * Creates an observable that emits a boolean value indicating whether the app is focused.
     *
     * @private
     * @returns {Observable<boolean>} An observable that emits true if the app is focused, false otherwise.
     */
    private _focusAppEvent(): Observable<boolean> {
        const focus$ = fromEvent(window, 'focus', { capture: true }).pipe(
            map(() => document.hasFocus())
        );
        const blur$ = fromEvent(window, 'blur', { capture: true }).pipe(
            startWith(null),
            map(() => document.hasFocus())
        );

        const visibilityChange$ = of(document).pipe(
            switchMap((doc) => {
                if ('webkitHidden' in doc) {
                    return fromEvent(doc, 'webkitvisibilitychange');
                }
                if ('msHidden' in doc) {
                    return fromEvent(doc, 'msvisibilitychange');
                }
                if ('mozHidden' in doc) {
                    return fromEvent(doc, 'mozvisibilitychange');
                }

                return fromEvent(doc, 'visibilitychange');
            }),
            map(() => document.visibilityState === 'visible')
        );
        return merge(focus$, blur$, visibilityChange$);
    }

    /**
     * Detect using app and show warning
     *
     * @return {*}  {void}
     * @memberof CommonService
     */
    public detectUsingApp(): void {
        if (this._isServer) {
            return;
        }
        const isEnabled$ = this._router.events.pipe(
            startWith(null),
            delay(100),
            filter((event) => event instanceof NavigationEnd || event === null),
            map(() => this.getCurrentRouteData(this._route.snapshot)),
            map((data) => data && !!data['idleCheck'])
        );
        const focusEvent$ = this._focusAppEvent();
        combineLatest([focusEvent$, isEnabled$])
            .pipe(
                startWith([document.hasFocus(), false]),
                distinctUntilChanged(),
                withLatestFrom(this._warningOpened$.asObservable()),
                switchMap(([[focused, enabled], opened]) => {
                    if (!focused && !opened && enabled) {
                        return interval(this.MAXIMUM_IDLE_TIME).pipe(take(1));
                    }
                    return of(null);
                }),
                filter((v) => v !== null),
                mergeMap(() => {
                    this._warningOpened$.next(true);
                    return this._dialog.confirm({
                        title: $localize`:@@common.toast.inactive-app.title:Cảnh báo`,
                        message: $localize`:@@common.toast.inactive-app.message:Bạn đã không hoạt động một thời gian dài. Vui lòng làm mới trang để tiếp tục sử dụng.`,
                        confirmText: $localize`:@@common.buttons.refresh:Tải lại`,
                        hideCancel: true,
                    });
                })
            )
            .subscribe(() => {
                this._warningOpened$.next(false);
                window.location.reload();
            });
    }

    /**
     * Get term of use
     *
     * @return {*}  {Observable<string>}
     * @memberof CommonService
     */
    public getTermOfUse(): Observable<string> {
        return this._httpClient.get(`/assets/data/terms-and-conditions.${this._localeId}.md`, {
            responseType: 'text',
        });
    }
}
